import React, { useEffect, useMemo, useState } from 'react';

import Slider from 'react-slick';

import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  Button,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Box,
  List,
  Stack,
  Divider,
  Link,
} from '@mui/material';

import Axios from '../../Assets/Axios/Axios';
import Banner from '../../Assets/Banners/1.png';
import Avatar1 from '../../Assets/Avatar/2.png';
import Avatar2 from '../../Assets/Avatar/1.png';
import GirlLapImg from '../../Assets/Random/1.png';
import { ShieldCheck, Star, Users, ArrowRight, Search } from 'lucide-react';

import { getCategoryIcon } from '../../Helpers/categoryIcon';

import Reveal from '../../Components/Reveal';
import { Stagger, StaggerItem, Counter, motion } from '../../Components/Motion';
import HomeImgCalender from '../../Assets/Random/2.png';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import { TypoWhite } from '../../Components/Typo';

import { COLORS } from '../../Constant/COLORS';

import { useIsMobile } from '../../Hooks/useIsMobile';

import { ServicesQuery, GetCategories } from '../../Server/Query';

import Slider1 from './Components/Slider1';
import Slider2 from './Components/Slider2';
import ItemIcoDesc from './Components/ItemIcoDesc';
import ListItemCalender from './Components/ListItemCalender';
import ListItemAreYouProf from './Components/ListItemAreYouProf';
import ItemAvatarProfessionals from './Components/ItemAvatarProfessionals';

import { toCapitalize } from '../../Helpers';

import 'swiper/css';
import 'swiper/css/navigation';

const EASE = [0.22, 1, 0.36, 1];

// Headline that reveals word-by-word.
const StaggerHeadline = ({ text, ...rest }) => (
  <Typography variant='h1' component='h1' sx={{ color: '#fff', maxWidth: 720 }} {...rest}>
    {text.split(' ').map((word, i) => (
      <Box
        key={`${word}-${i}`}
        component={motion.span}
        sx={{ display: 'inline-block', mr: '0.28em', whiteSpace: 'nowrap' }}
        initial={{ opacity: 0, y: '0.5em' }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE, delay: 0.15 + i * 0.08 }}
      >
        {word}
      </Box>
    ))}
  </Typography>
);

function Home() {
  const [selSlideAuthor, setSelSlideAuthor] = useState(0);
  const [services, setServices] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isSearchLoading, setIsSearchLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const isMobile = useIsMobile();
  const navigate = useNavigate();

  useEffect(() => {
    Axios(GetCategories).then(({ data, errors }) => {
      if (errors || !data?.categories) return undefined;
      setCategories(data.categories);
    });
  }, []);

  // Load services up front so the very first keystroke in the hero search has
  // suggestions to filter (no "open once to load" delay).
  useEffect(() => {
    setIsSearchLoading(true);
    Axios(ServicesQuery)
      .then(response => {
        if (response.errors) return undefined;
        setServices((response.data?.Services ?? []).map(parseService).filter(Boolean));
      })
      .finally(() => setIsSearchLoading(false));
  }, []);

  // Unified, grouped search suggestions: categories + professionals/services.
  const searchOptions = useMemo(() => {
    const categoryOptions = categories.map(category => ({
      type: 'category',
      group: 'Categories',
      id: `cat-${category.id}`,
      label: toCapitalize(category.name),
      to: `/category/${category.slug}`,
    }));
    const serviceOptions = services.map(item => ({
      type: 'service',
      group: 'Professionals',
      id: `svc-${item.id}`,
      label: item.professionalName
        ? `${item.professionalName} · ${item.name}`
        : item.name,
      serviceName: item.name,
      to: `/professional/${item.serviceId}`,
    }));
    return [...categoryOptions, ...serviceOptions];
  }, [categories, services]);

  function handleClickSearch() {
    // A picked suggestion goes straight to its page…
    if (selectedOption?.to) {
      navigate(selectedOption.to);
      return;
    }
    // …otherwise free text runs a full search on the results page.
    const query = inputValue.trim();
    if (query) navigate(`/search?q=${encodeURIComponent(query)}`);
  }

  const trustStats = [
    { Icon: Users, node: <><Counter value={500} suffix='+' /> professionals</> },
    { Icon: Star, node: <><Counter value={4.9} decimals={1} /> average rating</> },
    { Icon: ShieldCheck, node: <>Verified &amp; trusted</> },
  ];

  return (
    <>
      <Header isHomePage />

      {/* ── HERO ── */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 560, md: '92vh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component={motion.div}
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${Banner})`,
            backgroundColor: COLORS.tealDark,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        />
        <Box sx={{ position: 'absolute', inset: 0, background: COLORS.heroGradient }} />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            width: { xs: '90%', md: '80%' },
            mx: 'auto',
            py: { xs: 6, md: 0 },
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <Typography
              variant='overline'
              sx={{ color: 'rgba(255,255,255,0.85)', letterSpacing: '3px', mb: 2, display: 'block' }}
            >
              Sri Lanka&rsquo;s Event Professionals
            </Typography>
          </Box>

          <StaggerHeadline text='Find the perfect professional for your event' />

          <Box
            component={motion.p}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.55 }}
            sx={{
              color: 'rgba(255,255,255,0.9)',
              mt: 2.5,
              mb: 0,
              maxWidth: 500,
              fontSize: '1.05rem',
              lineHeight: 1.6,
            }}
          >
            Photographers, decorators, caterers and more — discover and book
            trusted talent for your special day.
          </Box>

          {/* Floating search pill */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.7 }}
          >
            <Stack
              direction='row'
              alignItems='center'
              mt={4}
              sx={{
                bgcolor: '#fff',
                borderRadius: 999,
                p: 0.75,
                pl: 2,
                width: 'fit-content',
                maxWidth: '100%',
                boxShadow: '0 20px 50px rgba(1,42,52,0.35)',
              }}
            >
              <Search size={20} color={COLORS.inkFaint} strokeWidth={1.8} />
              <Autocomplete
                freeSolo
                size='small'
                options={searchOptions}
                sx={{ width: { xs: 190, sm: 360 } }}
                loading={isSearchLoading}
                value={selectedOption}
                inputValue={inputValue}
                onInputChange={(_event, value) => setInputValue(value)}
                groupBy={option => option.group}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={option =>
                  typeof option === 'string' ? option : option.label
                }
                onChange={(_event, value) => {
                  setSelectedOption(value);
                  // Selecting a real suggestion navigates immediately.
                  if (value && typeof value !== 'string' && value.to) navigate(value.to);
                }}
                renderOption={(props, option) => {
                  const Icon = getCategoryIcon(
                    option.type === 'category' ? option.label : option.serviceName
                  );
                  return (
                    <Box
                      component='li'
                      {...props}
                      key={option.id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          borderRadius: '50%',
                          bgcolor: COLORS.tealSoft,
                          color: COLORS.teal,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon size={16} strokeWidth={1.8} />
                      </Box>
                      <Typography variant='body2' noWrap>
                        {option.label}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Search a service, professional or category'
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleClickSearch();
                      }
                    }}
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                )}
              />
              <Button
                color='blue'
                onClick={handleClickSearch}
                sx={{ px: 3.5, flexShrink: 0, borderRadius: 999 }}
              >
                Search
              </Button>
            </Stack>
          </Box>

          {/* Trust stats with count-up */}
          <Stack
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            direction='row'
            flexWrap='wrap'
            gap={{ xs: 2, md: 4 }}
            mt={4}
            sx={{ color: 'rgba(255,255,255,0.94)' }}
          >
            {trustStats.map(({ Icon, node }, i) => (
              <Stack key={i} direction='row' alignItems='center' gap={1}>
                <Icon size={18} strokeWidth={1.8} />
                <Typography variant='body2' sx={{ color: 'inherit', fontWeight: 600 }}>
                  {node}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>

      <Box mb={8}>
        {/* ── POPULAR SERVICES ── */}
        <Box mx='auto' maxWidth={{ xs: '90%', md: '80%' }} mt={{ xs: 6, md: 10 }} mb={3}>
          <Reveal>
            <Typography variant='overline' color={COLORS.teal} display='block'>
              Handpicked for you
            </Typography>
            <Typography variant='h3'>Popular professional services</Typography>
          </Reveal>
        </Box>
        <Box maxWidth={{ xs: '90%', md: 'calc(80% + 62px)' }} mx='auto' mb='30px'>
          <Slider1 />
        </Box>

        {/* ── WHOLE EVENT ── */}
        <Box sx={{ bgcolor: COLORS.surfaceAlt }} py={{ xs: 5, md: 8 }} mt={{ xs: 4, md: 6 }}>
          <Grid
            container
            justifyContent='center'
            alignItems='center'
            columnSpacing={{ md: 6 }}
            rowSpacing={4}
            maxWidth={{ xs: '90%', md: '80%' }}
            mx='auto'
            columns={{ xs: 1, md: 2 }}
          >
            <Grid item xs={1}>
              <Reveal>
                <Typography variant='overline' color={COLORS.teal} display='block'>
                  Why Evento
                </Typography>
                <Typography variant='h3' mb={2}>
                  A whole event of professionals at your fingertips
                </Typography>
              </Reveal>
              <Stagger>
                <List sx={{ position: 'relative' }}>
                  {[
                    ['Incredible choice', 'Whether you want to book professionals for a wedding, a birthday party, an engagement or any other event, Evento.lk gives you photographers, henna artists, fashion designers, throne arrangers, cake makers, hairdressers and more.'],
                    ['Affordable rates', 'Evento.lk guarantees the best available rates. And with our price-match promise you can rest assured you are always getting a great deal.'],
                    ['No reservation payment', "We don't charge any booking payment or administrative charges."],
                    ['24/7 availability', "We're available 24/7 to fulfil our customers' requirements, so you can book at any time."],
                  ].map(([title, body]) => (
                    <StaggerItem key={title}>
                      <ListItemCalender title={title}>{body}</ListItemCalender>
                    </StaggerItem>
                  ))}
                </List>
              </Stagger>
            </Grid>
            <Grid item xs={1}>
              <Reveal delay={0.1}>
                <Box
                  component='img'
                  src={HomeImgCalender}
                  alt='professionals'
                  sx={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: 4,
                    boxShadow: '0 24px 60px rgba(14,27,31,0.12)',
                  }}
                />
              </Reveal>
            </Grid>
          </Grid>
        </Box>

        {/* ── CATEGORIES ── */}
        <Box maxWidth={{ xs: '90%', md: '80%' }} mx='auto' mt={{ xs: 6, md: 10 }}>
          <Reveal>
            <Typography variant='overline' color={COLORS.teal} display='block' textAlign='center'>
              Browse by category
            </Typography>
            <Typography variant='h3' textAlign='center' mb={{ xs: 3, md: 5 }}>
              Professional services for every occasion
            </Typography>
          </Reveal>

          <Stagger>
            <Grid container columns={{ xs: 2, sm: 3, md: 5 }} spacing={{ xs: 2, md: 3 }}>
              {categories
                .filter(category => category.showHome)
                .slice(0, 10)
                .map(category => (
                  <Grid item xs={1} key={category.id}>
                    <StaggerItem style={{ height: '100%' }}>
                      <ItemIcoDesc
                        src={category.icon}
                        text={category.name}
                        to={`/category/${category.slug}`}
                        sx={{ height: '100%' }}
                      />
                    </StaggerItem>
                  </Grid>
                ))}
            </Grid>
          </Stagger>

          <Box textAlign='center' mt={{ xs: 4, md: 5 }}>
            <Button
              component={RouterLink}
              to='/categories'
              variant='outlined'
              size='large'
              endIcon={<ArrowRight size={18} strokeWidth={1.8} />}
              sx={{ color: COLORS.teal }}
            >
              View all categories
            </Button>
          </Box>
        </Box>

        {/* ── ARE YOU A PROFESSIONAL ── */}
        <Box
          sx={{
            mt: { xs: 6, md: 10 },
            py: { xs: 6, md: 9 },
            position: 'relative',
            overflow: 'hidden',
            background: COLORS.tealGradient,
          }}
        >
          {/* decorative soft glow */}
          <Box sx={{ position: 'absolute', top: -80, right: -60, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%)' }} />
          <Grid
            container
            maxWidth={{ xs: '90%', md: '80%' }}
            alignItems='center'
            mx='auto'
            spacing={4}
            sx={{ position: 'relative' }}
          >
            <Grid item xs={12} md={6}>
              <Reveal>
                <Box
                  component='img'
                  alt='become a professional'
                  src={GirlLapImg}
                  sx={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: 4 }}
                />
              </Reveal>
            </Grid>
            <Grid item xs={12} md={6}>
              <Reveal delay={0.1}>
                <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.75)' }}>
                  For professionals
                </Typography>
                <TypoWhite variant='h3' fontWeight={700} mb={1}>
                  Are you a professional?
                </TypoWhite>
                <TypoWhite variant='body1' sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Be a part of someone&rsquo;s big event. Get registered with{' '}
                  <Box component='span' sx={{ fontWeight: 700, textDecoration: 'underline' }}>
                    Evento.lk
                  </Box>
                </TypoWhite>
                <List sx={{ '& .MuiListItem-root': { color: '#fff' } }}>
                  <ListItemAreYouProf>Show your talent and advertise yourself easily</ListItemAreYouProf>
                  <ListItemAreYouProf>Enhance your career within a short span of time</ListItemAreYouProf>
                  <ListItemAreYouProf>Reach a wide range of customers across the island</ListItemAreYouProf>
                </List>
                <Button
                  component={RouterLink}
                  to='professional'
                  color='white1'
                  size='large'
                  endIcon={<ArrowRight size={18} strokeWidth={1.8} />}
                  sx={{ mt: 2, color: COLORS.teal, fontWeight: 700 }}
                >
                  Get Started
                </Button>
              </Reveal>
            </Grid>
          </Grid>
        </Box>

        {/* ── INSPIRATION ── */}
        <Box maxWidth={{ xs: '90%', md: '80%' }} mx='auto' mt={{ xs: 6, md: 10 }} mb={{ xs: 2, md: 4 }}>
          <Reveal>
            <Typography variant='overline' color={COLORS.teal} display='block'>
              Inspiration
            </Typography>
            <Typography variant='h3'>Get inspired by our professionals&rsquo; work</Typography>
          </Reveal>
        </Box>
        <Box maxWidth={{ xs: '90%', md: 'calc(80% + 62px)' }} mx='auto'>
          <Slider2 />
        </Box>

        {/* ── TESTIMONIALS ── */}
        <Box maxWidth={{ xs: '90%', md: '80%' }} mx='auto' mt={{ xs: 6, md: 10 }}>
          <Grid container columns={3} alignItems='flex-start' spacing={4}>
            <Grid item xs={3} md={1}>
              <Reveal>
                <Divider sx={{ width: 56, borderBottomWidth: 4, borderColor: COLORS.teal, borderRadius: 2, mb: 3 }} />
                <Typography variant='h3'>
                  What famous{' '}
                  <Box component='span' color={COLORS.teal} fontWeight={700}>
                    professionals
                  </Box>{' '}
                  say about{' '}
                  <Box component='span' color={COLORS.teal} fontWeight={700}>
                    Evento.lk
                  </Box>
                </Typography>
              </Reveal>
            </Grid>
            {(() => {
              const renderItems = [
                <ItemAvatarProfessionals
                  key={1}
                  px='1em'
                  md={1}
                  avatar={Avatar1}
                  name='Sam'
                  job='Photographer'
                  text={`Evento is a site for professionals to find work and book their services. It gives you the chance to collect reviews from previous clients, who can share their experiences with other users, and connects you with prospective clients.`}
                />,
                <ItemAvatarProfessionals
                  key={2}
                  px='1em'
                  md={1}
                  avatar={Avatar2}
                  name='Joshaf'
                  job='Fashion Designer'
                  text={`Evento is a website where you can find professionals and book them. You can create your own profile and add pictures. It's great for people who need help with their careers or want to get started in the industry.`}
                />,
              ];

              return isMobile ? (
                <Grid item xs={3} mb='2em'>
                  <Slider
                    arrows={false}
                    dots
                    appendDots={dots => (
                      <Stack direction='row' gap={2} justifyContent='center'>
                        {dots.map(dot => dot.props.children)}
                      </Stack>
                    )}
                    customPaging={index => (
                      <Box
                        boxSizing='border-box'
                        bgcolor={index === selSlideAuthor ? COLORS.teal : undefined}
                        border={index !== selSlideAuthor ? `2px solid ${COLORS.hairlineStrong}` : undefined}
                        width='30px'
                        height='7px'
                        borderRadius={2.5}
                      />
                    )}
                    beforeChange={(_prev, next) => setSelSlideAuthor(next)}
                    infinite={false}
                    slidesToShow={1}
                  >
                    {renderItems}
                  </Slider>
                </Grid>
              ) : (
                renderItems
              );
            })()}
          </Grid>
        </Box>
      </Box>
      <Footer />
    </>
  );
}

function parseService(service) {
  if (!service?.id) return null;

  return {
    id: service.id,
    serviceId: service.service_id ?? service.serviceId,
    name: service.name ? toCapitalize(service.name) : 'Service',
    professionalName: service.professional_name ?? '',
    categoryName: service.category_name ?? '',
    categorySlug: service.category_slug ?? '',
  };
}

export default Home;
